/*
 * Confidential and Proprietary.
 * Do not distribute without 1-800-Flowers.com, Inc. consent.
 * Copyright 1-800-Flowers.com, Inc. 2019. All rights reserved.
 */
/* eslint-disable  dot-notation */
import React from 'react';
import {
    bool, string, object, shape, oneOfType,
} from 'prop-types';
import { connect } from 'react-redux';
import { Link } from 'react-router-dom';
import { bindActionCreators } from 'redux';

import Grid from '@material-ui/core/Grid';
import CheckIcon from '@material-ui/icons/Check';
import withStyles from '@material-ui/core/styles/withStyles';

import { ducks as checkoutDucks } from '../../../../../../../../../state/ducks/Checkout';
import { getDataDictionary } from '../../../../../../../../../state/ducks/App/App-Selectors';
import { getFeatureFlags } from '../../../../../../../../../state/ducks/App/ducks/Config/Config-Selectors';

/* eslint-disable jsx-a11y/anchor-is-valid */
const {
    common: {
        commonSelectors,
    },
    recipient: { recipientActions },
} = checkoutDucks;

// pass a prop into breadcrumb that signals that a subheading is active
const styles = (theme) => ({
    breadcrumbRow: {
        margin: '7px 0 12px',
    },
    inactive: {
        color: theme.palette.colorNeutral60,
        fontSize: '16px',
        fontWeight: '600',
        cursor: 'pointer',
        '& span': {
            verticalAlign: 'top',
            display: 'inline-block',
        },
        [theme?.breakpoints?.down('md')]: {
            fontSize: '15px',
        },
    },
    active: {
        padding: '0px 18px 16px 6px',
        color: theme.palette.colorNeutral80,
        fontSize: '16px',
        fontWeight: '600',
        borderBottom: `4px solid ${theme.palette.colorPrimary}`,
        cursor: 'pointer',
        '& span': {
            verticalAlign: 'top',
            display: 'inline-block',
        },
        [theme?.breakpoints?.down('md')]: {
            fontSize: '15px',
        },
    },
    prefixIcon: {
        width: '23px',
        height: '23px',
        lineHeight: '16px',
        fontSize: '16px',
        margin: '0 8px 0 10px',
        borderRadius: '50%',
        padding: '2px 6px 5px 2px',
        display: 'inline-block',
        backgroundColor: theme.palette.primaryButton,
    },
    filledCheckMark: {
        color: theme.palette.white,
        fontSize: '18px',
    },
    prefixNumber: {
        backgroundColor: theme.palette.colorPrimary,
        color: theme.palette.white,
        width: '23px',
        height: '24px',
        lineHeight: '16px',
        fontSize: '16px',
        margin: '0 8px 0 10px',
        borderRadius: '50%',
        padding: '5px',
        display: 'inline-block',
    },
    hidden: {
        display: 'none',
    },
});
const checkPaypalCheckout = (payload) => (payload && payload.nonce && payload.pageType === 'cart');

const getCurrentStep = (active) => {
    let currentStep = 1;
    if (active === 'shipping-date') currentStep = 2;
    if (active === 'payment') currentStep = 3;
    if (active === 'review') currentStep = 4;
    return currentStep;
};

const getGridDivider = (isShippingDateTabEnabled, isPaypalCheckoutFromCart) => {
    const gridItemDivider = isShippingDateTabEnabled ? 3 : 4;
    return isPaypalCheckoutFromCart ? 6 : gridItemDivider;
};

const checkIsActive = (active, classes, label) => (active === label ? classes.active : classes.inactive);

const checkIsVisible = (isPaypalCheckoutFromCart, hiddenClass) => (isPaypalCheckoutFromCart ? hiddenClass : '');

const Breadcrumb = ({
    classes, active, orderId,
    continueToPayment, continueToReview, paypal,
    dataDictionary, featureFlag,
}) => {
    const getPrefix = (index, isIcon) => (
        <span className={isIcon ? classes.prefixIcon : classes.prefixNumber}>
            {isIcon ? <CheckIcon className={classes.filledCheckMark} /> : index + 1}
        </span>
    );
    const { payload } = paypal;
    const isPaypalCheckoutFromCart = checkPaypalCheckout(payload);
    const isShippingDateTabEnabled = featureFlag['is-checkout-shipping-date-enabled'];
    const gridItemDivider = getGridDivider(isShippingDateTabEnabled, isPaypalCheckoutFromCart);

    const currentStep = getCurrentStep(active);

    return (
        <Grid container className={classes.breadcrumbRow}>
            <Grid item xs={gridItemDivider} align="center">
                <Link
                    className={checkIsActive(active, classes, 'shipping')}
                    to={((currentStep > 1) ? `/checkout/shipping/${orderId}` : '#')}
                    data-test="checkout-breadcrumb-shipping"
                >
                    {getPrefix(0, continueToPayment)}
                    <span>{dataDictionary?.['Delivery'] || 'Delivery'}</span>
                </Link>
            </Grid>
            {
                isShippingDateTabEnabled
                    ? (
                        <Grid item xs={gridItemDivider} align="center" className={checkIsVisible(isPaypalCheckoutFromCart, classes.hidden)}>
                            <Link
                                className={checkIsActive(active, classes, 'shipping-date')}
                                to={((currentStep > 2 || continueToPayment) ? `/checkout/shipping-date/${orderId}` : '#')}
                            >
                                {getPrefix(1, continueToReview)}
                                <span>{dataDictionary?.['Shipping'] || 'Shipping'}</span>
                            </Link>
                        </Grid>
                    ) : ''
            }
            {/* Hide Payment page if users use PayPal checkout from cart page */}
            <Grid item xs={gridItemDivider} align="center" className={checkIsVisible(isPaypalCheckoutFromCart, classes.hidden)}>
                <Link
                    className={checkIsActive(active, classes, 'payment')}
                    to={((currentStep > 3 || continueToPayment) ? `/checkout/payment/${orderId}` : '#')}
                >
                    {getPrefix(isShippingDateTabEnabled ? 2 : 1, continueToReview)}
                    <span>{dataDictionary?.['Payment'] || 'Payment'}</span>
                </Link>
            </Grid>
            <Grid item xs={gridItemDivider} align="center">
                <Link
                    className={(active === 'review' || active === 'review-order') ? classes.active : classes.inactive}
                    to={((currentStep > 4 || continueToReview) ? `/checkout/review-order/${orderId}` : '#')}
                >
                    {getPrefix(isShippingDateTabEnabled ? 3 : 2, continueToReview)}
                    <span>{dataDictionary?.['Review'] || 'Review'}</span>
                </Link>
            </Grid>
        </Grid>
    );
};

Breadcrumb.defaultProps = {
    dataDictionary: {},
};

Breadcrumb.propTypes = {
    classes: object.isRequired,
    active: string.isRequired,
    orderId: string.isRequired,
    featureFlag: object.isRequired,
    continueToPayment: bool.isRequired,
    continueToReview: bool.isRequired,
    dataDictionary: object,
    paypal: shape({
        payload: oneOfType([string, object]),
        clientToken: string,
    }).isRequired,
};

const mapStateToProps = (state) => ({
    featureFlag: getFeatureFlags(state),
    dataDictionary: getDataDictionary(state),
    orderId: commonSelectors.getOrderId(state),
    paypal: commonSelectors.getPaypalPaymentDetails(state),
    continueToPayment: commonSelectors.continueToPayment(state),
    continueToReview: commonSelectors.getReviewOrderStatus(state),
    orderItems: commonSelectors.getRecipientsExcludePassport(state),
});

const mapDispatchToProps = (dispatch) => ({
    triggerValidateOrder: bindActionCreators(recipientActions.callToTriggerValidateOrder, dispatch),
});

export default connect(mapStateToProps, mapDispatchToProps)(withStyles(styles)(Breadcrumb));
